import { z } from 'zod';
// ── Company Visitor ID region policy ─────────────────────────────────────────
// Governs where an anonymous visitor may be resolved to a company WITHOUT
// explicit analytics consent (the no-CMP / permissive-consent fallback).
// Consent is always PRIMARY: an explicitly-consented visitor resolves in any
// region (the CMP — e.g. Concord's region templates — already applied the
// region-appropriate banner). This policy only decides the no-explicit-consent
// case, keyed on the visitor's geoip country (+ optional subdivision), mirroring
// Concord's region model ({country, subDivision, consentMode}).
/** What the no-explicit-consent fallback does in a region. */
export const VISITOR_ID_REGION_MODES = ['opt_out', 'require_consent'];
export const VISITOR_ID_REGION_PRESETS = [
    'us_only', // resolve only US visitors without consent (default; safest simple)
    'gdpr', // resolve everywhere EXCEPT EEA/UK without consent
    'strict', // never resolve without explicit consent (consent-only)
    'global', // resolve everywhere without consent (org accepts the risk)
    'custom', // per-country/subdivision rules below
];
// A custom rule: ISO-3166-1 alpha-2 country, optional subdivision (state /
// province, as geoip reports it — e.g. 'CA' for California), and the mode.
// Most-specific wins: (country + subdivision) beats country-only.
export const visitorIdCustomRegionSchema = z.object({
    country: z
        .string()
        .trim()
        .length(2)
        .transform((c) => c.toUpperCase()),
    subDivision: z
        .string()
        .trim()
        .min(1)
        .max(8)
        .transform((s) => s.toUpperCase())
        .optional(),
    mode: z.enum(VISITOR_ID_REGION_MODES),
});
export const visitorIdRegionPolicySchema = z.object({
    preset: z.enum(VISITOR_ID_REGION_PRESETS),
    // custom-preset rules (ignored for other presets). Capped: this is a policy
    // table, not a geo database.
    custom: z.array(visitorIdCustomRegionSchema).max(200).optional(),
    // What an UNLISTED region does under the custom preset. Defaults to
    // require_consent (fail safe).
    customDefault: z.enum(VISITOR_ID_REGION_MODES).optional(),
});
export const DEFAULT_VISITOR_ID_REGION_POLICY = {
    preset: 'us_only',
};
// EEA (EU-27 + Iceland, Liechtenstein, Norway) + the UK (UK GDPR). ISO-3166-1
// alpha-2, matching what geoip reports (Greece = GR, United Kingdom = GB).
export const EEA_UK_COUNTRIES = new Set([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
    'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
    'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    'IS', 'LI', 'NO',
    'GB',
]);
const norm = (value) => {
    const v = value?.trim().toUpperCase();
    return v ? v : null;
};
// US state name → code, keyed by upper-cased full name. Rules store codes
// ("CA"), but one geo source gives the code and the other gives the full name
// ("California"), so without this map a US+CA rule would miss half the time.
// US-only; other countries match on the value as-is.
const US_STATE_NAME_TO_CODE = {
    ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR',
    CALIFORNIA: 'CA', COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE',
    FLORIDA: 'FL', GEORGIA: 'GA', HAWAII: 'HI', IDAHO: 'ID', ILLINOIS: 'IL',
    INDIANA: 'IN', IOWA: 'IA', KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA',
    MAINE: 'ME', MARYLAND: 'MD', MASSACHUSETTS: 'MA', MICHIGAN: 'MI',
    MINNESOTA: 'MN', MISSISSIPPI: 'MS', MISSOURI: 'MO', MONTANA: 'MT',
    NEBRASKA: 'NE', NEVADA: 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC',
    'NORTH DAKOTA': 'ND', OHIO: 'OH', OKLAHOMA: 'OK', OREGON: 'OR',
    PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD', TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT',
    VERMONT: 'VT', VIRGINIA: 'VA', WASHINGTON: 'WA', 'WEST VIRGINIA': 'WV',
    WISCONSIN: 'WI', WYOMING: 'WY', 'DISTRICT OF COLUMBIA': 'DC',
    'PUERTO RICO': 'PR', GUAM: 'GU', 'U.S. VIRGIN ISLANDS': 'VI',
    'AMERICAN SAMOA': 'AS', 'NORTHERN MARIANA ISLANDS': 'MP',
};
// The forms an incoming subdivision may match a stored rule by: the normalized
// value, plus (for the US) its 2-letter code when the value arrived as a full
// name. Total (never throws); an unmapped value yields just the normalized form.
const subDivisionMatchForms = (country, subDivision) => {
    const s = norm(subDivision);
    if (!s)
        return [];
    const code = country === 'US' ? US_STATE_NAME_TO_CODE[s] : undefined;
    return code && code !== s ? [s, code] : [s];
};
/**
 * Resolve what the no-explicit-consent fallback does for a visitor's region.
 * Pure + shared (backend gate, admin UI preview, tests).
 *
 * Unknown country: fail safe — `require_consent` under every preset except
 * `global` (an org on `global` has accepted resolving without geo certainty);
 * under `custom` the unlisted-default applies.
 */
export function resolveVisitorRegionMode(policy, country, subDivision) {
    const c = norm(country);
    switch (policy.preset) {
        case 'global':
            return 'opt_out';
        case 'strict':
            return 'require_consent';
        case 'us_only':
            return c === 'US' ? 'opt_out' : 'require_consent';
        case 'gdpr':
            if (!c)
                return 'require_consent'; // could be EEA — fail safe
            return EEA_UK_COUNTRIES.has(c) ? 'require_consent' : 'opt_out';
        case 'custom': {
            const fallback = policy.customDefault ?? 'require_consent';
            if (!c)
                return fallback;
            const forms = subDivisionMatchForms(c, subDivision);
            const rules = policy.custom ?? [];
            // Most-specific first: country + subdivision (matched on either the raw
            // normalized value or its resolved code), then country-only.
            if (forms.length > 0) {
                const exact = rules.find((r) => r.country === c &&
                    r.subDivision !== undefined &&
                    forms.includes(r.subDivision));
                if (exact)
                    return exact.mode;
            }
            const countryRule = rules.find((r) => r.country === c && !r.subDivision);
            if (countryRule)
                return countryRule.mode;
            return fallback;
        }
    }
}
//# sourceMappingURL=visitor-id.js.map